import PropTypes from 'prop-types';

import { Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import AsyncTableComponent from '../Table/AsyncTableContainer';

import ExpandableImageComponent from '../Image/ExpandableImageComponent';

import { avatarPlaceholderSRC, formatDate } from '../../utils';
import { getFullName } from '../Person/utils';
import { buildingsForFilterTypes } from '../Shared/utils/types';
import { getExportTitle } from '../ViewExport/utils';

import { TABLE_CONSTANTS, TABLE_ID } from '../Table/constants';

import {
  DATE_RANGES_TYPES,
  TABLE_DATA_TYPES,
  fullTimeDateFormat,
} from '../../staticData';
import StyledLink from '../StyledLink';
import CellPersonWithImage from '../Table/CellPersonWithImage';
import ListActions from '../Table/ListActions';
import { buildingFilter, limitFilter } from '../Table/filters';
import Tooltip from '../Tooltip';
import BuildingPopUpsExport from './BuildingPopUpsExport';

const BuildingPopUpsComponent = (props) => {
  const {
    onLoad,
    items,
    getItems,
    listParams,
    listKey,
    onExport,
    buildings,
    showUpdatePopUpModal,
    deletePopUp,
  } = props;
  const theme = useTheme();
  const columns = [
    {
      Header: 'Building',
      id: 'building',
      sortable: false,
      accessor: ({ building: id }, isExport) => {
        if (isExport) return buildings?.[id]?.name;
        return (
          <StyledLink to={`/building/${id}`}>
            {buildings?.[id]?.name}
          </StyledLink>
        );
      },
    },
    {
      Header: 'Headline',
      id: 'headline',
      sortable: false,
      accessor: ({ headline }, isExport) => {
        if (isExport) return headline;
        return <Tooltip tooltip={headline}>{headline}</Tooltip>;
      },
    },
    {
      Header: 'Body',
      id: 'body',
      sortable: false,
      accessor: ({ body }, isExport) => {
        if (isExport) return body;
        return <Tooltip tooltip={body}>{body}</Tooltip>;
      },
    },
    {
      Header: 'Photo preview',
      id: 'photo',
      sortable: false,
      width: 140,
      accessor: ({ photo }, isExport) => {
        if (isExport) return null;
        return (
          <ExpandableImageComponent
            src={avatarPlaceholderSRC}
            photo={photo}
            isClickable={Boolean(photo)}
            height="3.2rem"
            isSquare
          />
        );
      },
    },
    {
      Header: 'Created date',
      id: 'created_at',
      headerClassName: 'sort',
      defaultSortDesc: true,
      width: 200,
      accessor: ({ createdAt }) =>
        formatDate(createdAt, true, fullTimeDateFormat),
    },
    {
      Header: 'Created by',
      id: 'created_by',
      sortable: false,
      accessor: ({ creator }, isExport) => {
        if (isExport) return getFullName(creator);
        const linkUrl = `/global-person-search/${creator?.id}`;
        return <CellPersonWithImage person={creator} linkUrl={linkUrl} />;
      },
    },
    {
      Header: 'Edit button',
      id: 'edit',
      sortable: false,
      width: 150,
      accessor: (entry) => (
        <Button
          variant="contained"
          color="inherit"
          disableElevation
          onClick={() => showUpdatePopUpModal(entry)}
          fullWidth
        >
          Edit
        </Button>
      ),
      show: false,
    },
    {
      Header: 'Delete button',
      id: 'delete',
      sortable: false,
      width: 150,
      accessor: (entry) => (
        <Button
          variant="contained"
          color="secondary"
          disableElevation
          onClick={() => deletePopUp(entry?.id)}
          fullWidth
        >
          Delete
        </Button>
      ),
      show: false,
    },
    {
      Header: 'Actions',
      id: 'actions',
      fixed: 'right',
      accessor: (entry) => {
        const action = [
          {
            label: 'Edit',
            isFullWidth: true,
            onClick: () => showUpdatePopUpModal(entry),
          },
          {
            label: 'Delete',
            isFullWidth: true,
            onClick: () => deletePopUp(entry?.id),
            color: theme.palette.secondary.main,
          },
        ];
        return <ListActions actions={action} isFullWidth />;
      },
      width: 120,
      sortable: false,
    },
  ];

  const filters = [
    buildingFilter(listParams, buildings),
    limitFilter(listParams),
  ];

  const defaultParams = {
    offset: '0',
  };

  const action = null;
  const fieldsForBackendSort = [TABLE_CONSTANTS.created_at];
  const tableDataType = TABLE_DATA_TYPES.items;

  const title = 'Building Pop Up';
  const exportTitle = getExportTitle(title);
  const exportActions = (
    <BuildingPopUpsExport
      title={exportTitle}
      getDataForExport={onExport}
      columns={columns}
    />
  );

  return (
    <AsyncTableComponent
      tableId={TABLE_ID.remoteDoormanPopUp}
      title={title}
      columns={columns}
      defaultSort={TABLE_CONSTANTS.created_at}
      fieldsForBackendSort={fieldsForBackendSort}
      filters={filters}
      showFiltersAreApplied={false}
      onLoad={onLoad}
      items={items}
      getItems={getItems}
      listParams={listParams}
      defaultParams={defaultParams}
      tableDataType={tableDataType}
      listKey={listKey}
      action={action}
      dateRangeType={DATE_RANGES_TYPES.created_at}
      datePicker
      manual
      defaultSortDesc
      exportActions={exportActions}
      isHideSearch
    />
  );
};

BuildingPopUpsComponent.propTypes = {
  onLoad: PropTypes.func.isRequired,
  getItems: PropTypes.func.isRequired,
  items: PropTypes.instanceOf(Object).isRequired,
  listParams: PropTypes.instanceOf(Object).isRequired,
  listKey: PropTypes.string.isRequired,
  onExport: PropTypes.func.isRequired,
  showUpdatePopUpModal: PropTypes.func.isRequired,
  deletePopUp: PropTypes.func.isRequired,
  buildings: buildingsForFilterTypes.isRequired,
};

export default BuildingPopUpsComponent;
